/*
 * Gemeinsame Eingabebausteine des Eigenschaftsfelds.
 *
 * Absicht: die 22 typspezifischen Editoren sollen ueber Felder reden, nicht
 * ueber Markup. Alles, was mehr als einmal vorkommt (Beschriftung,
 * Pflichtmarke, Hinweistext, Fehlermarke, Listen mit Hinzufuegen/Entfernen/
 * Umsortieren), steht hier und nicht dort.
 *
 * Aufbau und Klassennamen folgen `components/WidgetConfigEditor.tsx`, damit
 * das Formular des Dashboards und das des HMI-Editors sich gleich anfuehlen.
 * Uebernommen wurden von dort: der Feldrahmen mit Beschriftung, Pflichtmarke
 * und Hilfetext, die Zahleneingabe mit Grenzen aus dem Schema, die
 * Listenzeile mit Hoch/Runter/Entfernen und der Umgang mit der leeren
 * Eingabe (leer heisst "nicht gesetzt", nicht "null").
 *
 * Nicht uebernommen wurde der Ansatz selbst: das Dashboard baut sein Formular
 * zur Laufzeit aus JSON Schema. Die HMI-Typen tragen strukturierte
 * Unterobjekte, und ein generischer Formularbauer macht daraus Listen von
 * Textfeldern.
 */

import type { ComponentChildren, JSX } from 'preact';
import { BindingField } from './BindingField';
import './fields.css';

// ------------------------------------------------------------------- Rahmen

export interface WidgetEditorProps<W> {
  widget: W;
  plantId: string;
  /** Feldnamen, deren Pflichtangabe noch fehlt. Kommt aus
   *  `missingRequiredFields` und wird einmal je Auswahl berechnet, nicht in
   *  jedem Editor neu. */
  missing: ReadonlySet<string>;
  onChange(next: W): void;
}

/** Grenzen, die keine sind. Ein Rundinstrument mit min groesser max ist ein
 *  Fehler, den man beim Eintippen sehen soll und nicht auf der Anlage. */
export function rangeError(min: number | undefined, max: number | undefined): string | undefined {
  if (min === undefined || max === undefined) return undefined;
  if (min === max) return 'Unten und oben sind gleich, die Skala hat keine Ausdehnung.';
  if (min > max) return 'Unten liegt über oben.';
  return undefined;
}

/** Eine Schrittweite von null laesst den Steller stehen, eine negative
 *  laeuft rueckwaerts. Beides faellt sonst erst dem Bediener auf. */
export function stepError(step: number | null | undefined): string | undefined {
  if (step === undefined || step === null) return undefined;
  if (step <= 0) return 'Die Schrittweite muss größer als null sein.';
  return undefined;
}

/** Erzeugt den Aenderungs-Helfer, den jeder Editor braucht: ein Teilstueck
 *  rein, das ganze Widget raus. */
export function patcher<W extends object>(
  widget: W,
  onChange: (next: W) => void,
): (part: Partial<W>) => void {
  return (part) => onChange({ ...widget, ...part });
}

export function Section(props: {
  title: string;
  hint?: string;
  children: ComponentChildren;
}) {
  return (
    <section class="hv-pf__section">
      <h4 class="hv-pf__section-title">{props.title}</h4>
      {props.hint && <p class="hv-pf__section-hint">{props.hint}</p>}
      <div class="hv-pf__fields">{props.children}</div>
    </section>
  );
}

/** Zwei Felder nebeneinander, solange die Breite es hergibt. Fuer Paare, die
 *  ohnehin zusammen gelesen werden (min/max, Wert/Farbe). */
export function Row(props: { children: ComponentChildren }) {
  return <div class="hv-pf__row">{props.children}</div>;
}

export interface FieldProps {
  label: string;
  required?: boolean;
  /** Fehlt eine Pflichtangabe, wird sie markiert. Gesperrt wird nichts:
   *  ein halbfertiges Widget muss speicherbar bleiben. */
  missing?: boolean;
  /** Eingegebener Unsinn, der jetzt sichtbar werden soll (min groesser max,
   *  Schrittweite null). */
  error?: string;
  hint?: string;
  children: ComponentChildren;
}

export function Field(props: FieldProps) {
  const cls = 'hv-pf__field'
    + (props.missing ? ' hv-pf__field--missing' : '')
    + (props.error ? ' hv-pf__field--error' : '');
  return (
    <div class={cls}>
      <label class="hv-pf__label">
        {props.label}
        {props.required && <span class="hv-pf__required" title="Pflichtangabe">*</span>}
      </label>
      {props.children}
      {props.error && <div class="hv-pf__error" role="alert">{props.error}</div>}
      {!props.error && props.hint && <div class="hv-pf__help">{props.hint}</div>}
      {!props.error && props.missing && (
        <div class="hv-pf__missing-note">Fehlt noch.</div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- Felder

export function TextField(props: {
  label: string;
  value: string | undefined;
  onChange(next: string | undefined): void;
  placeholder?: string;
  required?: boolean;
  missing?: boolean;
  hint?: string;
  /** Leere Eingabe als leerer String zurueckgeben statt als undefined. Fuer
   *  Felder, die im Typ nicht optional sind (etwa `circuitName`). */
  keepEmptyString?: boolean;
}) {
  return (
    <Field label={props.label} required={props.required} missing={props.missing} hint={props.hint}>
      <input
        class="hv-pf__input"
        type="text"
        value={props.value ?? ''}
        placeholder={props.placeholder}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value;
          props.onChange(raw === '' && !props.keepEmptyString ? undefined : raw);
        }}
      />
    </Field>
  );
}

export function NumberField(props: {
  label: string;
  value: number | null | undefined;
  onChange(next: number | undefined): void;
  min?: number;
  max?: number;
  step?: number;
  integer?: boolean;
  unit?: string;
  required?: boolean;
  missing?: boolean;
  error?: string;
  hint?: string;
}) {
  return (
    <Field
      label={props.unit ? `${props.label} (${props.unit})` : props.label}
      required={props.required}
      missing={props.missing}
      error={props.error}
      hint={props.hint}
    >
      <input
        class="hv-pf__input"
        type="number"
        value={props.value ?? ''}
        min={props.min}
        max={props.max}
        step={props.step ?? (props.integer ? 1 : 'any')}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value;
          if (raw === '') { props.onChange(undefined); return; }
          const n = props.integer ? parseInt(raw, 10) : parseFloat(raw);
          props.onChange(Number.isFinite(n) ? n : undefined);
        }}
      />
    </Field>
  );
}

export function CheckboxField(props: {
  label: string;
  checked: boolean | undefined;
  onChange(next: boolean): void;
  hint?: string;
}) {
  const on = props.checked === true;
  return (
    <div class="hv-pf__field hv-pf__field--check">
      <label class="hv-pf__check">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => props.onChange((e.currentTarget as HTMLInputElement).checked)}
        />
        <span>{props.label}</span>
      </label>
      {props.hint && <div class="hv-pf__help">{props.hint}</div>}
    </div>
  );
}

export interface SelectOption<T extends string> { value: T; label: string; }

export function SelectField<T extends string>(props: {
  label: string;
  value: T | undefined;
  options: readonly SelectOption<T>[];
  onChange(next: T): void;
  hint?: string;
}) {
  return (
    <Field label={props.label} hint={props.hint}>
      <select
        class="hv-pf__input"
        value={props.value ?? props.options[0]?.value ?? ''}
        onChange={(e) => props.onChange((e.currentTarget as HTMLSelectElement).value as T)}
      >
        {props.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

/**
 * Farbe. Der Waehler und ein Textfeld nebeneinander, weil das Schema an
 * dieser Stelle einen freien String erlaubt: ein Kunde traegt dort auch
 * `var(--status-warn)` ein, und ein reiner Farbwaehler koennte das nicht
 * darstellen, ohne es beim ersten Klick zu zerstoeren.
 */
export function ColourField(props: {
  label: string;
  value: string | undefined;
  onChange(next: string | undefined): void;
  hint?: string;
}) {
  const isHex = typeof props.value === 'string' && /^#[0-9a-fA-F]{6}$/.test(props.value);
  return (
    <Field label={props.label} hint={props.hint}>
      <div class="hv-pf__colour">
        <input
          class="hv-pf__colour-swatch"
          type="color"
          value={isHex ? props.value : '#8a929e'}
          onInput={(e) => props.onChange((e.currentTarget as HTMLInputElement).value)}
          aria-label={`${props.label}: Farbwähler`}
        />
        <input
          class="hv-pf__input"
          type="text"
          value={props.value ?? ''}
          placeholder="#77a22b"
          onInput={(e) => {
            const raw = (e.currentTarget as HTMLInputElement).value;
            props.onChange(raw === '' ? undefined : raw);
          }}
        />
      </div>
    </Field>
  );
}

// ------------------------------------------------------------ Freie Werte

/**
 * Ein Feld fuer die `unknown`-Werte des Schemas (Impulswert, Ein/Aus-Wert,
 * Aufzaehlungswert, Sichtbar-wenn).
 *
 * Diese Werte gehen unveraendert auf die Anlage. Ob dort `true`, `1` oder
 * `"on"` ankommen muss, weiss nur der Regler, und ein Textfeld, das alles zu
 * Strings macht, schreibt `"1"` in ein Integer-Register: das quittiert der
 * Feld-Client mit einem Typfehler, den niemand mehr dem Eigenschaftsfeld
 * zuordnet. Darum wird hier geparst und der erkannte Typ danebengestellt.
 */
export function ScalarField(props: {
  label: string;
  value: unknown;
  onChange(next: unknown): void;
  required?: boolean;
  missing?: boolean;
  hint?: string;
}) {
  const text = formatScalar(props.value);
  return (
    <Field
      label={props.label}
      required={props.required}
      missing={props.missing}
      hint={props.hint}
    >
      <div class="hv-pf__scalar">
        <input
          class="hv-pf__input"
          type="text"
          value={text}
          placeholder="true, 1, 21.5 oder Text"
          onInput={(e) => props.onChange(parseScalar((e.currentTarget as HTMLInputElement).value))}
        />
        <span class="hv-pf__scalar-type" title="So geht der Wert auf die Anlage">
          {describeScalar(props.value)}
        </span>
      </div>
    </Field>
  );
}

/** Text zu Wert. Leer bleibt leer (undefined), `true`/`false` werden bool,
 *  Zahlen werden Zahl, alles andere bleibt Text. Wer einen Text braucht, der
 *  wie eine Zahl aussieht, setzt ihn in Anfuehrungszeichen. */
export function parseScalar(raw: string): unknown {
  const trimmed = raw.trim();
  if (trimmed === '') return undefined;
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null') return null;
  if (trimmed.length >= 2 && trimmed.startsWith('"') && trimmed.endsWith('"')) {
    return trimmed.slice(1, -1);
  }
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return raw;
}

/** Wert zu Text, so dass `parseScalar` denselben Wert zurueckgibt. */
export function formatScalar(value: unknown): string {
  if (value === undefined) return '';
  if (value === null) return 'null';
  if (typeof value === 'boolean' || typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    // Ein Text, der wie ein bool, eine Zahl oder null aussieht, braucht
    // Anfuehrungszeichen, sonst kaeme er beim naechsten Lesen als Zahl
    // zurueck.
    return /^(true|false|null|-?\d+(\.\d+)?)$/.test(value.trim()) ? `"${value}"` : value;
  }
  return JSON.stringify(value);
}

function describeScalar(value: unknown): string {
  if (value === undefined) return 'leer';
  if (value === null) return 'null';
  switch (typeof value) {
    case 'boolean': return 'Bool';
    case 'number': return 'Zahl';
    case 'string': return 'Text';
    default: return 'Objekt';
  }
}

// ----------------------------------------------------------------- Bindungen

export interface BindingFilter {
  writable?: boolean;
  valueType?: 'number' | 'bool' | 'enum' | 'string';
}

/**
 * Bindungsfeld fuer die Pflichtbindungen (`commandPoint: string` und
 * Geschwister).
 *
 * Hier und nur hier liegt die Umrechnung zwischen den beiden Sprachen: das
 * Widget kennt den leeren String als "noch nicht gebunden" (siehe `UNBOUND`
 * in `palette/widgetCatalog.ts`), `BindingField` kennt dafuer `undefined`.
 * `'' -> undefined` hin, `undefined -> ''` zurueck. Wer diese Umrechnung an
 * einer zweiten Stelle nachbaut, erzeugt frueher oder spaeter Widgets mit
 * `undefined` in einem Pflichtfeld, und das faellt erst beim Speichern auf
 * dem Server auf.
 */
export function BindingRow(props: {
  label: string;
  value: string;
  plantId: string;
  filter?: BindingFilter;
  hint?: string;
  onChange(next: string): void;
}) {
  // Kein eigener Fehlt-Hinweis: `BindingField` zieht ihn aus `required` und
  // dem leeren Wert selbst. Zweimal dasselbe zu markieren macht die Spalte
  // unruhig und faellt beim Lesen nicht mehr auf.
  return (
    <div class="hv-pf__binding">
      <BindingField
        label={props.label}
        value={props.value === '' ? undefined : props.value}
        plantId={props.plantId}
        required
        filter={props.filter}
        hint={props.hint}
        onChange={(next) => props.onChange(next ?? '')}
      />
    </div>
  );
}

/** Bindungsfeld fuer die optionalen Bindungen (`feedbackPoint?: string`).
 *  Der leere String waere hier eine Luege: das Feld ist nicht "leer
 *  gebunden", es ist nicht vorhanden. */
export function OptionalBindingRow(props: {
  label: string;
  value: string | undefined;
  plantId: string;
  filter?: BindingFilter;
  hint?: string;
  onChange(next: string | undefined): void;
}) {
  return (
    <div class="hv-pf__binding">
      <BindingField
        label={props.label}
        value={props.value === '' ? undefined : props.value}
        plantId={props.plantId}
        filter={props.filter}
        hint={props.hint}
        onChange={(next) => props.onChange(next === '' ? undefined : next)}
      />
    </div>
  );
}

// -------------------------------------------------------------- Listenfelder

export interface ListEditorProps<T> {
  label: string;
  hint?: string;
  items: readonly T[];
  onChange(next: T[]): void;
  /** Neuer, leerer Eintrag. Bewusst eine Funktion und kein Objekt: sonst
   *  teilen sich alle hinzugefuegten Zeilen dieselbe Referenz. */
  create(): T;
  addLabel?: string;
  emptyHint?: string;
  required?: boolean;
  missing?: boolean;
  /** Ueberschrift der Zeile, damit man in einer langen Liste weiss, wo man
   *  ist, ohne jedes Feld zu lesen. */
  itemTitle?(item: T, index: number): string;
  renderItem(item: T, patch: (part: Partial<T>) => void, index: number): ComponentChildren;
}

/**
 * Liste aus strukturierten Unterobjekten: Zonen, Optionen, Kurven, Stufen,
 * Knoten, Kanten, Animationen.
 *
 * Umsortieren ist hier kein Luxus. Bei Aufzaehlungen und Kaskaden steht die
 * Reihenfolge auf dem Schirm, und wer eine Stufe vergessen hat, will sie
 * nicht loeschen und dreimal neu tippen muessen.
 */
export function ListEditor<T>(props: ListEditorProps<T>) {
  const items = props.items;
  const emit = (next: T[]) => props.onChange(next);

  const patchAt = (index: number) => (part: Partial<T>) => {
    const next = items.slice();
    next[index] = { ...(next[index] as object), ...(part as object) } as T;
    emit(next);
  };
  const removeAt = (index: number) => emit(items.filter((_, i) => i !== index));
  const moveBy = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const held = next[index];
    next[index] = next[target];
    next[target] = held;
    emit(next);
  };
  const add = () => emit([...items, props.create()]);

  const cls = 'hv-pf__list' + (props.missing ? ' hv-pf__list--missing' : '');

  return (
    <div class={cls}>
      <div class="hv-pf__list-head">
        <span class="hv-pf__label">
          {props.label}
          {props.required && <span class="hv-pf__required" title="Pflichtangabe">*</span>}
          <span class="hv-pf__list-count">{items.length}</span>
        </span>
        <button type="button" class="hv-pf__list-add" onClick={add}>
          {props.addLabel ?? 'Hinzufügen'}
        </button>
      </div>
      {props.hint && <div class="hv-pf__help">{props.hint}</div>}
      {items.length === 0 && (
        <p class="hv-pf__list-empty">
          {props.emptyHint ?? 'Noch nichts eingetragen.'}
        </p>
      )}
      {items.map((item, index) => (
        <div class="hv-pf__list-item" key={index}>
          <div class="hv-pf__list-item-head">
            <span class="hv-pf__list-item-title">
              {props.itemTitle?.(item, index) ?? `#${index + 1}`}
            </span>
            <span class="hv-pf__list-item-tools">
              <button
                type="button"
                class="hv-pf__icon-btn"
                title="Nach oben"
                aria-label="Nach oben"
                disabled={index === 0}
                onClick={() => moveBy(index, -1)}
              >↑</button>
              <button
                type="button"
                class="hv-pf__icon-btn"
                title="Nach unten"
                aria-label="Nach unten"
                disabled={index === items.length - 1}
                onClick={() => moveBy(index, 1)}
              >↓</button>
              <button
                type="button"
                class="hv-pf__icon-btn hv-pf__icon-btn--danger"
                title="Entfernen"
                aria-label="Entfernen"
                onClick={() => removeAt(index)}
              >×</button>
            </span>
          </div>
          <div class="hv-pf__fields">{props.renderItem(item, patchAt(index), index)}</div>
        </div>
      ))}
    </div>
  );
}

/** Freitextfeld mit Vorschlagsliste. Fuer Werte, die wir kennen koennten,
 *  aber nicht erzwingen duerfen (Element-Ids aus dem SVG, Achsengruppen). */
export function SuggestField(props: {
  label: string;
  value: string | undefined;
  suggestions: readonly string[];
  listId: string;
  onChange(next: string | undefined): void;
  placeholder?: string;
  required?: boolean;
  missing?: boolean;
  hint?: string;
  keepEmptyString?: boolean;
}): JSX.Element {
  return (
    <Field label={props.label} required={props.required} missing={props.missing} hint={props.hint}>
      <input
        class="hv-pf__input"
        type="text"
        list={props.suggestions.length > 0 ? props.listId : undefined}
        value={props.value ?? ''}
        placeholder={props.placeholder}
        onInput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value;
          props.onChange(raw === '' && !props.keepEmptyString ? undefined : raw);
        }}
      />
      {props.suggestions.length > 0 && (
        <datalist id={props.listId}>
          {props.suggestions.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </Field>
  );
}
